import type { Role } from './AuthContext';

export interface Division {
  divisionId: string;
  name: string;
  /** Abteilungskürzel aus der Kostenstellen-Bezeichnung, z.B. "FB". */
  abbreviation?: string;
  active: boolean;
  /** Hauptverein: keine Budgetrücklage (D-22). */
  mainClub?: boolean;
}

export interface AppUser {
  email: string;
  role: Role;
  divisionIds: string[];
  active: boolean;
  /** `lokal` = eigenes MyBudget-Konto, `myhours` = Anmeldung mit dem MyHours-Konto. */
  kind: 'lokal' | 'myhours';
  givenName?: string;
  familyName?: string;
  createdAt: string;
  createdBy: string;
}

export interface Status {
  /** Zeitpunkt des letzten erfolgreichen SevDesk-Imports, null solange es keinen gab. */
  dataAsOf: string | null;
}

export const ROLE_LABELS: Record<Role, string> = {
  admin: 'Admin',
  vorstand: 'Vorstand',
  abteilungsleiter: 'Abteilungsleiter',
};

export interface ImportRun {
  startedAt: string;
  finishedAt?: string;
  trigger: string;
  mode: 'hourly' | 'full';
  years: string[];
  status: 'ok' | 'error' | 'skipped';
  message?: string;
  vouchers?: number;
  vouchersChanged?: number;
  positionsFetched?: number;
  written?: number;
  removed?: number;
  closedYearDeviations?: number;
  requests?: number;
  durationMs?: number;
}

export interface ImportOverview {
  status: { lastRunAt?: string; lastSuccessAt?: string; lastError?: string; lastErrorAt?: string };
  runs: ImportRun[];
}

export type AccountArt = 'anlage' | 'abschreibung' | 'umsatzsteuer';

export interface AccountEntry {
  number: string;
  name: string;
  art?: AccountArt;
  groupId?: string;
}

/** Report-Gruppe (D-11) */
export interface GroupEntry {
  groupId: string;
  name: string;
  kind?: 'einnahme' | 'ausgabe';
  order?: number;
}

export interface CategoryEntry {
  categoryId: string;
  name: string;
  skr04?: string;
  accountNumber?: string;
  /** Konto, das tatsächlich gilt (Zuordnung des Vereins, sonst SKR04 von SevDesk) */
  effectiveAccount?: string;
  suggestion?: string;
}

export interface CostCentreEntry {
  number: string;
  name?: string;
  divisionId?: string;
  taxArea?: string;
  /** D-24: Umsatzsteuer erlaubt (nur im Wirtschafts- oder Zweckbetrieb wirksam) */
  vatAllowed?: boolean;
  /** Abteilung/Bereich, wie sie gelten -- Eintrag oder abgeleitet aus der Bezeichnung */
  derived: { divisionId?: string; taxArea?: string };
}

export interface TaxAreaEntry {
  key: string;
  name: string;
}

export interface AccountRule {
  accountNumber: string;
  costCentreNumber: string;
}

export interface MappingsOverview {
  accounts: AccountEntry[];
  categories: CategoryEntry[];
  costCentres: CostCentreEntry[];
  taxAreas: TaxAreaEntry[];
  accountRules: AccountRule[];
  voucherRules: { voucherId: string; costCentreNumber: string; voucherNumber?: string }[];
  groups: GroupEntry[];
  divisions: Division[];
}

export interface UnassignedGroup {
  problem: 'kategorie' | 'kostenstelle';
  key: string;
  label: string;
  count: number;
  sumCents: number;
  examples: {
    voucherId: string;
    voucherNumber?: string;
    stichtag: string;
    amountCents: number;
    text?: string;
  }[];
}

export interface ImportCsvResult {
  imported: number;
  removed?: number;
  createdDivisions?: string[];
  createdGroups?: string[];
  errors: string[];
}
